import {Component, Input, OnInit, OnDestroy, ViewChild, ChangeDetectorRef} from '@angular/core';
import moment from 'moment';
import { RequestTemplateService } from '../../../services/request-template/request-template.service';
import {Router} from "@angular/router";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { TemplateConfirmationModalComponent } from '../../../components/template-confirmation-modal/template-confirmation-modal.component';
import {AlertBannerService, AlertType} from "../../../lib/alert-banner";
import {TemplateRenameModalComponent} from "../../../components/template-rename-modal/template-rename-modal.component";
import {PermissionsService} from "../../../services/permissions/permissions.service";


@Component({
  selector: 'app-request-template',
  templateUrl: './request-template.component.pug',
  styleUrls: ['./request-template.component.scss'],
  standalone: false
})
export class RequestTemplateComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  @Input() org: any;

  dropDownOptions = [
    {key: 1, value: $localize`View package`, divider: false },
    {key: 2, value: $localize`Edit package`, divider: false },
    {key: 3, value: $localize`Activate package`, divider: false },
    {key: 4, value: $localize`Deactivate package`, divider: false },
    {key: 5, value: $localize`Delete package`, divider: true }
  ];
  disableTooltip = $localize`This package was automatically disabled because the survey or background checks are no longer available.`
  templates: any[] = [];
  loading = false;
  templatesLoaded = false;
  operationLoading = false;
  label = '';
  sortString = '-modified';
  total = 0;
  currentPage = 1;
  rowsPerView = 20;
  searchTitle = $localize`Packages will appear here.`;
  searchDescription = $localize`Packages save time by providing consistent, predefined settings that instantly apply to new requests. Visibility is based on product permissions and survey access.`;
  createBtn = $localize`Create a package`
  @ViewChild('sortDropdown') sortDropdown:any;
  get groupDropDownOptions() {
    return [
        {
          'key': $localize`Times used`,
          'value': [
            {'label': $localize`Highest`, 'sortString': '-usage'},
            {'label': $localize`Lowest`, 'sortString': 'usage'}
          ]
        },

        {
            key: 'group-end',
            value: []
        },
        {
            key: $localize`Title`,
            value: [
              {'label': $localize`A-Z`, 'sortString': 'name'},
              {'label': $localize`Z-A`, 'sortString': '-name'}
            ]
        },
        {
            key: 'group-end',
            value: []
        },
        {
          'key': $localize`Last updated`,
          'value': [
            { 'label': $localize`Newest`, 'sortString': '-modified' },
            { 'label': $localize`Oldest`, 'sortString': 'modified' }
          ]
        },
    ];
  }

  private bsModalRef!: BsModalRef;
  canManageTemplate = false;
  canCreateTemplate = false;
  backgroundMember = false;
  referenceMember = false;
  backgroundAdmin = false;
  referenceAdmin = false;

  constructor(
    public router: Router,
    private templateService: RequestTemplateService,
    private cdr: ChangeDetectorRef,
    private modalService: BsModalService,
    private alertList: AlertBannerService,
    private permissions: PermissionsService,
  ) { }

  ngOnInit(): void {
    // Reset loading state to ensure loader shows when component is initialized
    this.templatesLoaded = false;
    this.operationLoading = false;
    
    this.canCreateTemplate = this.permissions.hasPermission("can_manage_package") ||
        this.permissions.hasPermission("can_manage_reference_package") ||
        this.permissions.hasPermission("can_manage_backgroundcheck_package")
    this.canManageTemplate = this.permissions.hasPermission("can_manage_package")
    this.backgroundMember = this.permissions.hasPermission("can_view_use_backgroundcheck_package")
    this.referenceMember = this.permissions.hasPermission("can_view_use_reference_package_with_survey_permission")
    this.backgroundAdmin = this.permissions.hasPermission("can_manage_backgroundcheck_package")
    this.referenceAdmin = this.permissions.hasPermission("can_manage_reference_package")

    this.label = `${this.groupDropDownOptions[4].key} (${this.groupDropDownOptions[4].value[0].label})`;

    this.getAndProcessTemplatesList();

  }

  getAndProcessTemplatesList(): void {
    this.loading = true;
    this.templatesLoaded = false;

    this.templateService.listTemplates(this.rowsPerView, this.currentPage, '', this.sortString).pipe(
        takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        // Use requestAnimationFrame to ensure update happens after current frame
        // This avoids ExpressionChangedAfterItHasBeenCheckedError
        requestAnimationFrame(() => {
          this.processTemplates(response['templates'] || response['data'] || []);
          this.total = response['total'] || response['count'] || 0;
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        requestAnimationFrame(() => {
          this.templatesLoaded = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
        this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
        }]);
      },
      complete: () => {
      }
    });
  }

  private processTemplates(templates: any[]): void {
    // Process templates: extract data, format dates, set kebab options
    this.templates = templates.map(template => {
      // Find the background config block
      const backgroundConfig = template.config?.find(c => c.type === 'background');

      // Extract partner check names from partner_checks array
      const backgroundChecks = (backgroundConfig?.partner_checks?.map((check: any) => check.name) ?? []).join(', ');

      // Find the reference config block
      const referenceConfig = template.config?.find(c => c.type === 'reference');

      // Extract package_name (if present)
      const request = referenceConfig?.package_name || '';

      // Format created date to "22 Sept 2025" format
      const created = this.formatDate(template.created);


      // Get kebab menu options based on current_state
      const kebab_options = this.getKebabOptions(template.current_state, referenceConfig, backgroundConfig,
          template?.has_package_permission, (template?.usage_count ?? 0) > 0);

      // Add a new property to your template object
      return {
        ...template,
        backgroundChecks,
        request,
        created,
        kebab_options
      };
    });
  }

  private formatDate(date: string | null | undefined): string | null {
    if (!date) {
      return null;
    }
    // Format date from "YYYY-MM-DD" to "D MMM YYYY" (e.g., "22 Sept 2025")
    return moment(date, [moment.ISO_8601, 'YYYY-MM-DD'], true).format('D MMM YYYY');
  }

  private getKebabOptions(currentState: string, referenceConfig, backgroundConfig, package_permission, is_used:boolean): any[] {
    const options: any[] = [];
    if(this.canManageTemplate || (this.backgroundAdmin && this.referenceAdmin))
    {
        switch (currentState) {
          case 'disabled':

            options.push({ key: 6, value: $localize`Rename package`, divider: false });
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if(!is_used){
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            break;
          case 'inactive_unused':
            // Edit package, Activate package, Delete package
            options.push({ key: 2, value: $localize`Edit package`, divider: false });
            options.push({ key: 3, value: $localize`Activate package`, divider: false });
            options.push({ key: 5, value: $localize`Delete package`, divider: true });
            break;
          case 'inactive_used':
            options.push({ key: 6, value: $localize`Rename package`, divider: false });
            options.push({ key: 1, value: $localize`View package`, divider: false });
            options.push({ key: 3, value: $localize`Activate package`, divider: false });
            break;
          case 'active_used':
            options.push({ key: 6, value: $localize`Rename package`, divider: false });
            options.push({ key: 1, value: $localize`View package`, divider: false });
            options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
            break;
          case 'active_unused':
            // Edit package, Deactivate package, Delete package
            options.push({ key: 2, value: $localize`Edit package`, divider: false });
            options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
            options.push({ key: 5, value: $localize`Delete package`, divider: true });
            break;
          default:
            // Default: no option
            break;
        }
    }
    else if(this.referenceAdmin){
        switch (currentState) {
          case 'disabled':
            if(!backgroundConfig || !this.backgroundMember){
                options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if((!backgroundConfig || !this.backgroundMember) && !is_used){
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            break;
          case 'inactive_unused':
            // Edit package, Activate package, Delete package
            if(!backgroundConfig || !this.backgroundMember){
                options.push({ key: 2, value: $localize`Edit package`, divider: false });
                options.push({ key: 3, value: $localize`Activate package`, divider: false });
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            else if(this.backgroundMember && backgroundConfig){
                options.push({ key: 1, value: $localize`View package`, divider: false });
            }
            break;
          case 'inactive_used':
            if(!backgroundConfig || !this.backgroundMember){
                options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if(!backgroundConfig || !this.backgroundMember){
                options.push({ key: 3, value: $localize`Activate package`, divider: false });
            }
            break;
          case 'active_used':
            if(!backgroundConfig || !this.backgroundMember){
                  options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if(!backgroundConfig || !this.backgroundMember ){
                options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
            }
            break;
          case 'active_unused':
            if(!backgroundConfig || !this.backgroundMember){
                options.push({ key: 2, value: $localize`Edit package`, divider: false });
                options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            else if(this.backgroundMember && backgroundConfig){
                options.push({ key: 1, value: $localize`View package`, divider: false });
            }
            break;
          default:
            break;
        }
    }
    else if(this.backgroundAdmin){
        switch (currentState) {
          case 'disabled':
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if((!this.referenceMember || !referenceConfig) && !is_used){
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            break;
          case 'inactive_unused':
            // Edit package, Activate package, Delete package
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 2, value: $localize`Edit package`, divider: false });
                options.push({ key: 3, value: $localize`Activate package`, divider: false });
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            else if(this.referenceMember && referenceConfig){
                options.push({ key: 1, value: $localize`View package`, divider: false });
            }
            break;
          case 'inactive_used':
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 3, value: $localize`Activate package`, divider: false });
            }
            break;
          case 'active_used':
            if(!this.referenceMember || !referenceConfig){
                  options.push({ key: 6, value: $localize`Rename package`, divider: false });
            }
            options.push({ key: 1, value: $localize`View package`, divider: false });
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
            }
            break;
          case 'active_unused':
            if(!this.referenceMember || !referenceConfig){
                options.push({ key: 2, value: $localize`Edit package`, divider: false });
                options.push({ key: 4, value: $localize`Deactivate package`, divider: false });
                options.push({ key: 5, value: $localize`Delete package`, divider: true });
            }
            else if(this.referenceMember && referenceConfig){
                options.push({ key: 1, value: $localize`View package`, divider: false });
            }
            break;
          default:
            break;
        }
    }

    return options;
  }

  onCreateNew(): void {
    this.router.navigateByUrl('/template');
  }

  onSortChange(event: any): void {
    this.sortString = event.sortString;
    this.label = `${event.key} (${event.label})`;
    // Reset to first page when sorting changes
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }

  getStatusClass(status: string): 'neutral' | 'amber' | 'cherry' | 'forest' | 'citrus' | 'fill-forest' | 'black' | 'disabled' | 'white' | '' | null {
    switch (status) {
      case 'active_unused':
      case 'active_used':
        return 'forest';
      case 'inactive_unused':
      case 'inactive_used':
        return 'neutral';
      case 'disabled':
        return 'amber';
      default:
        return 'neutral';
    }
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'disabled':
        return 'https://assets.xref.com/omni/shared/alert_amber.svg';
      default:
        return '';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'active_unused':
      case 'active_used':
        return $localize`Active`;
      case 'inactive_unused':
      case 'inactive_used':
        return $localize`Inactive`;
      case 'disabled':
        return $localize`Disabled`;
      default:
        return $localize`Inactive`;
    }
  }

  getTimesUsedText(timesUsed: number | null | undefined): string {
    if (timesUsed === 0 || timesUsed === null || timesUsed === undefined) {
      return '0';
    }
    return timesUsed.toString();
  }

  checkOverflow(element: any): boolean {
    if (!element) {
      return false;
    }
    const el = element.nativeElement || element;
    if (!el || typeof el.offsetWidth === 'undefined' || typeof el.scrollWidth === 'undefined') {
      return false;
    }
    return el.offsetWidth < el.scrollWidth;
  }


  CardMenuEvent(event: {key: number}, template: any): void {
    switch (event.key) {
      case 1:
        // View package
        this.onViewTemplate(template);
        break;
        
      case 2:
        // Edit package
        this.onEditTemplate(template);
        break;
        
      case 3:
        // Activate package
        this.onActivateTemplate(template);
        break;
        
      case 4:
        // Deactivate package
        this.onDeactivateTemplate(template);
        break;
        
      case 5:
        // Delete package
        this.onDeleteTemplate(template);
        break;
      case 6:
        // Rename package
        this.onRenameTemplate(template);
        break;
        
      default:
        break;
    }
  }

  private onViewTemplate(template: any): void {
     this.router.navigateByUrl('/template', {
      state: {
        template: template,
        isViewMode: true
      }
    });
  }

  private onEditTemplate(template: any): void {
    this.router.navigateByUrl('/template', {
      state: {
        template: template,
        isEditMode: true
      }
    });
  }

  private onActivateTemplate(template: any): void {
    this.operationLoading = true;
    this.templateService.activateTemplate(template.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        // Update template state
        if (response) {
          // Find the template in the array and update it
          const templateIndex = this.templates.findIndex(t => t.id === template.id);
          if (templateIndex !== -1) {
            const existingTemplate = this.templates[templateIndex];
            const backgroundConfig = existingTemplate.config?.find(c => c.type === 'background');
            const referenceConfig = existingTemplate.config?.find(c => c.type === 'reference');

            // Create a new object with updated properties to ensure change detection
            const updatedTemplate = {
              ...existingTemplate,
              current_state: response.current_state,
              is_active: response.is_active,
              kebab_options: this.getKebabOptions(response.current_state, referenceConfig, backgroundConfig, existingTemplate?.has_package_permission, (response?.usage_count ?? 0) > 0)
            };
            
            // Update the array with the new object reference
            this.templates = [
              ...this.templates.slice(0, templateIndex),
              updatedTemplate,
              ...this.templates.slice(templateIndex + 1)
            ];
            this.cdr.detectChanges();
          }
          this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The package, ${template.name}, has been activated.`,
              keepOnRouteChange: true
          }]);
        }
        this.operationLoading = false;
      },
      error: (error) => {
        this.operationLoading = false;
        this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
        }]);
      }
    });
  }

  private onDeactivateTemplate(template: any): void {
    const initialState = {
      title: $localize`Deactivate package`,
      message: $localize`By deactivating this package, it will become unavailable for future requests within the Xref platform and Xref integrations.\n\nAre you sure you want to deactivate this package?`,
      confirmButtonText: $localize`Deactivate`,
      templateName: template.name || ''
    };

    this.bsModalRef = this.modalService.show(TemplateConfirmationModalComponent, {
      class: 'modal-dialog-centered m-popup',
      initialState
    });

    this.bsModalRef.content.confirmClick.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.operationLoading = true;
      this.templateService.deactivateTemplate(template.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          // Update template state
          if (response) {
            // Find the template in the array and update it
            const templateIndex = this.templates.findIndex(t => t.id === template.id);
            if (templateIndex !== -1) {
              const existingTemplate = this.templates[templateIndex];
              const backgroundConfig = existingTemplate.config?.find(c => c.type === 'background');
              const referenceConfig = existingTemplate.config?.find(c => c.type === 'reference');

              // Create a new object with updated properties to ensure change detection
              const updatedTemplate = {
                ...existingTemplate,
                current_state: response.current_state,
                is_active: response.is_active,
                kebab_options: this.getKebabOptions(response.current_state, referenceConfig, backgroundConfig, existingTemplate?.has_package_permission, (response?.usage_count ?? 0) > 0)
              };
              
              // Update the array with the new object reference
              this.templates = [
                ...this.templates.slice(0, templateIndex),
                updatedTemplate,
                ...this.templates.slice(templateIndex + 1)
              ];
              this.cdr.detectChanges();
            }
            this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The package, ${template.name}, has been deactivated.`,
              keepOnRouteChange: true
            }]);
          }
          this.operationLoading = false;
        },
        error: (error) => {
          this.operationLoading = false;
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      });
    });
  }

  private onRenameTemplate(template: any): void {
    const initialState = {
      rename:true,
      title: $localize`Rename package`,
      subHeading: $localize`Assign a unique title that will identify this package across the platform and integrations.`,
      templateName: template.name || '',
      templateId: template.id
    };

    this.bsModalRef = this.modalService.show(TemplateRenameModalComponent, {
      class: 'modal-dialog-centered m-popup',
      initialState
    });

    this.bsModalRef.content?.closeEvent?.pipe(takeUntil(this.destroy$)).subscribe((newTemplateName: string) => {
      // Refresh templates list to show updated name
      // The getAndProcessTemplatesList() method already handles loading state
      this.getAndProcessTemplatesList();
      this.alertList.append([{
          type: AlertType.SUCCESS,
          message: $localize`Success! The package, ${newTemplateName}, has been saved.`,
          keepOnRouteChange: true
      }]);

    });
  }

  private onDeleteTemplate(template: any): void {
    const initialState = {
      title: $localize`Delete package`,
      message: $localize`By deleting this package, it will be permanently removed from the account and unavailable for future requests within the Xref platform and Xref integrations.\n\nAre you sure you want to delete this package?`,
      confirmButtonText: $localize`Delete`,
      templateName: template.name || ''
    };

    this.bsModalRef = this.modalService.show(TemplateConfirmationModalComponent, {
      class: 'modal-dialog-centered m-popup',
      initialState
    });

    this.bsModalRef.content.confirmClick.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.operationLoading = true;
      this.templateService.deleteTemplate(template.id).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.operationLoading = false;
          // Reload templates list (this will show its own loading state)
          this.getAndProcessTemplatesList();
          this.alertList.append([{
              type: AlertType.SUCCESS,
              message: $localize`Success! The package, ${template.name}, has been deleted.`,
              keepOnRouteChange: true
            }]);
        },
        error: (error) => {
          this.operationLoading = false;
          this.alertList.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      });
    });
  }

  onPageChange(page: number): void {
    if (this.currentPage !== page) {
      this.currentPage = page;
      this.getAndProcessTemplatesList();
    }
  }

  onPageSizeChange(rows: number): void {
    this.rowsPerView = rows;
    this.currentPage = 1;
    this.getAndProcessTemplatesList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isDisabled(state: string): boolean {
      return ['disabled'].includes(state);
  }

}

